/**
 * The Metrics tab's Filter, applied in memory: the tab fetches every record
 * the session holds in one request, so filtering never goes back to the
 * server. The fields are the record's own, plus `group`, which matches a
 * dotted prefix of the name.
 */

import {
  filterInMemory,
  matchesDate,
  matchesOptions,
  matchesString,
  type InMemoryFilterParams,
} from "@/shared/components/filters";
import type { Filter } from "@/shared/components/ResourceView";
import type { MetricRecord } from "@/shared/domain/metrics";

/** The filter builder's rows and how they combine. */
export type MetricFilterParams = InMemoryFilterParams;

/** Whether `name` is in the group `group`: equal to it, or beneath it. */
function inGroup(name: string, _comparator: string, group: string): boolean {
  return name === group || name.startsWith(`${group}.`);
}

const FILTER_MATCHERS = {
  name: (record, filter) =>
    filter.type === "string" && matchesString(record.name, filter),
  group: (record, filter) =>
    filter.type === "string" && matchesString(record.name, filter, inGroup),
  published: (record, filter) =>
    filter.type === "date" && matchesDate(record.published, filter),
  invocation_id: (record, filter) =>
    filter.type === "enum" && matchesOptions(record.invocation_id, filter),
  device_id: (record, filter) =>
    filter.type === "enum" && matchesOptions(record.device_id, filter),
} satisfies Record<string, (record: MetricRecord, filter: Filter) => boolean>;

/** A field the Metrics tab can be filtered on. */
export type MetricFilterField = keyof typeof FILTER_MATCHERS;

/**
 * The records that pass the filter builder's rows, joined by `matchMode` (AND
 * requires every row, OR at least one; no rows pass everything). Filtering
 * records rather than metrics keeps a metric's record count and latest value
 * true to the filter: narrowed to one invocation, a metric shows that
 * invocation's records only.
 */
export function filterMetricRecords(
  records: readonly MetricRecord[],
  params: MetricFilterParams,
): MetricRecord[] {
  return filterInMemory(records, params, FILTER_MATCHERS);
}
